import { Button, Kind } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import {
  useDeleteUserListMutation,
  useGetUserListCampaignsQuery,
} from 'api/userLists'
import { getErrorMessage, isFetchBaseQueryError } from 'api/utils'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('user_lists.landing.delete_modal')
const tCancel = buildTranslate('components.confirm_modal')

type Props = {
  isOpen: boolean
  onClose: () => void
  listId: string
  listName: string
}

const DeleteListModal: React.FC<Props> = ({
  isOpen,
  onClose,
  listId,
  listName,
}) => {
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const [deleteList, { isLoading: isDeleting }] = useDeleteUserListMutation()
  const { data: campaignsData, isLoading: isLoadingCampaigns } =
    useGetUserListCampaignsQuery(
      { userListId: Number(listId), state: 'active' },
      { skip: !isOpen }
    )

  const activeCampaigns = campaignsData?.data ?? []
  const isBlocked = activeCampaigns.length > 0

  const handleConfirm = async () => {
    try {
      await deleteList(listId).unwrap()
      onClose()
      dispatch(
        alert({
          key: 'success',
          message: t('success'),
          timeout: 5,
        })
      )
    } catch (error) {
      const message = isFetchBaseQueryError(error)
        ? getErrorMessage(error, t('error'))
        : t('error')
      dispatch(
        alert({
          key: 'danger',
          message,
          timeout: 5,
        })
      )
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      closeType="small"
      zIndex={1200}
    >
      <div
        className={`text-center p-xl d-flex flex-column container ${css({
          maxWidth: '600px',
        })}`}
      >
        <Spinner isLoading={isLoadingCampaigns} className="text-center py-xl">
          {isBlocked ? (
            <>
              <h3 className="m-0">{t('blocked_title', { name: listName })}</h3>
              <p className="body-text-3 grey-70 mt-xs">
                {t('blocked_description')}
              </p>
              <Notification kind="warning" size="full">
                <div className="text-left p-xs">
                  <Text.BodyText4 bold className="mb-xs" tag="p">
                    {t('blocked_campaigns_label')}
                  </Text.BodyText4>
                  <ul className="mb-0 pl-l">
                    {activeCampaigns.map(c => (
                      <li key={c.id}>
                        <Text.BodyText4 color="grey70">{c.name}</Text.BodyText4>
                      </li>
                    ))}
                  </ul>
                </div>
              </Notification>
              <div className="d-flex mt-m">
                <Button className="flex-1" onClick={onClose}>
                  {t('blocked_ok')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className="m-0">{t('title')}</h3>
              <p
                className="body-text-3 grey-70 mt-xs"
                dangerouslySetInnerHTML={{
                  __html: t('description', { name: listName }),
                }}
              />
              <div className="d-flex mt-m">
                <Button
                  kind={Kind.SECONDARY}
                  onClick={onClose}
                  className="flex-1 mr-m"
                >
                  {tCancel('cancel')}
                </Button>
                <Button
                  className="flex-1"
                  isLoading={isDeleting}
                  onClick={handleConfirm}
                >
                  {t('confirm')}
                </Button>
              </div>
            </>
          )}
        </Spinner>
      </div>
    </Modal>
  )
}

export default DeleteListModal
